
<template>
    <div data-id="asset">
        <div class="row pb-0">
            <h3 class="col-md-12">
                {{ editableAssetProperties.name }}
            </h3>
        </div>
        <alerts :status="alert"
            v-model="alert"
            class="categories-alerts">
        </alerts>
        <transition name="fade">
            <div v-if="deleteAssetModal" class="modal-wrapper">
                <div class="modal-bg" @click="deleteAssetModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <div class="row mx-4 pb-1">
                        <p class="font-weight-bold">
                            Are you sure you want to delete this asset?
                        </p>
                    </div>
                    <div class="row mx-4 my-0 pb-0">
                        <div class="col-md-6 d-flex align-items-center">
                            <button class="btn my-2 btn-outline-primary" @click="deleteAssetModal = false">
                                Cancel
                            </button>
                            <button data-id="asset-delete-submit" class="btn ml-2 btn-danger" @click="confirmDeleteAsset">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <div class="row dam-asset__wrapper mt-2 pr-4">
            <div class="dam-asset__preview-column m-0 p-0 pr-4 w-100 col-md-5">
                <div class="dam-asset__asset-preview w-100">
                    <img v-if="assetTypesMap.displayableImageTypes.includes(assetTransform.asset_type_id) && assetTransform.uri_internal"
                        :src="assetTransform.uri_internal"
                        id="lightbox-toggle"
                        @click="lightbox = true" 
                        data-id="asset-lightbox-toggle"
                    />
                    <div v-else class="asset-preview__icon-outer" @click="lightbox = true" >
                        <div class="asset-preview__icon-inner d-flex align-items-center justify-content-center">
                            <i v-if="assetTypesMap.imagePlaceholderTypes.includes(assetTransform.asset_type_id)"
                                class="fa fa-file-image-o fa-4x text-dark"
                                aria-hidden="true"
                            ></i>
                            <i v-else-if="assetTypesMap.displayableVideoTypes.includes(assetTransform.asset_type_id)"
                                class="fa fa-file-video-o fa-4x text-dark"
                                aria-hidden="true"
                            ></i>
                            <i v-else-if="assetTypesMap.videoPlaceholderTypes.includes(assetTransform.asset_type_id)"
                                class="fa fa-file-video-o fa-4x text-dark"
                                aria-hidden="true"
                            ></i>
                            <i v-else class="fa fa-file-o fa-4x text-dark" aria-hidden="true"></i>
                        </div>
                    </div>
                    <lightbox v-if="lightbox"
                        @lightbox-closed="handleCloseLightbox"
                        :content="assetTransform"
                        :asset-types-map="assetTypesMap"
                        :is-safari="isSafari"
                    ></lightbox>
                </div>
                <div style="font-size: 12px; width: 100%; max-width: 24rem">
                    <div style="color: #79858d" class="mb-4">
                        Click on the thumbnail to open full-size in lightbox
                    </div>
                    <div class="mt-2 d-flex align-items-center justify-content-between">
                        <span>Date created:</span>
                        <span><strong>{{ assetTransform.created_at }}</strong></span>
                    </div>
                    <div class="mt-2 d-flex align-items-center justify-content-between">
                        <span>Last modified:</span>
                        <span><strong>{{ assetTransform.updated_at }}</strong></span>
                    </div>
                    <div class="mt-2 d-flex align-items-center justify-content-between">
                        <span>Size:</span>
                        <span><strong>{{ assetTransform.size_readable }}</strong></span>
                    </div>
                    <div class="mt-2 d-flex align-items-center justify-content-between">
                        <span>Size:</span>
                        <span><strong>{{ assetTransform.size_readable }}</strong></span>
                    </div>
                </div>
            </div>
            <div class="dam-asset__info-column col-md-7 p-0 placard">
                <div class="row w-100 pb-0 mx-0" style="border-bottom: 1px solid #dedede;">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <button class="nav-link" :class="{ 'active': selectedTab === 'general' }" @click="selectedTab = 'general'">
                                General
                            </button>
                        </li>
                        <li class="nav-item" v-if="permissions.includes('edit_dam') && assetTransform.master_asset_id == null">
                            <button data-id="asset-link" class="nav-link" :class="{ 'active': selectedTab === 'linkeditems' }" @click="selectedTab = 'linkeditems'">
                                Linked Items
                            </button>
                        </li>
                        <li class="nav-item" v-if="features.includes('dam_advanced') && assetTransform.master_asset_id == null">
                            <button data-id="asset-versions" class="nav-link" :class="{ 'active': selectedTab === 'versions' }" @click="selectedTab = 'versions'">
                                Versions
                            </button>
                        </li>
                        <li class="nav-item" v-if="features.includes('dam_advanced') && assetTransform.master_asset_id == null && assetTypesMap.displayableImageTypes.includes(assetTransform.asset_type_id)">
                            <button data-id="asset-transformations" class="nav-link" :class="{ 'active': selectedTab === 'transformations' }" @click="selectedTab = 'transformations'">
                                Transformations
                            </button>
                        </li>
                    </ul>
                </div>
                <div class="mb-4 mx-0 pb-0 row w-100" v-if="selectedTab === 'general'">
                    <div class="row w-100 my-0 pb-0 col-md-12">
                        <div class="col-md-12 mb-4" v-if="permissions.includes('edit_dam')">
                            <p class="text-muted">
                                Editable properties for the current asset
                            </p>
                        </div>
                    </div>
                    <div class="row mt-0 w-100 pb-0 col-md-12">
                        <div class="col-md-6 d-flex flex-column">
                            <label for="name">Name</label>
                            <input type="text"
                                class="form-control"
                                v-model="editableAssetProperties.name"
                                data-id="asset-name"
                                :disabled="permissions.length && !permissions.includes('edit_dam')"
                            />
                        </div>
                        <div class="col-md-6">
                            <label>Folder</label>
                            <select-folder
                                @selection="handleFolderSelection"
                                data-id="asset-folder"
                                :disabled="permissions.length && !permissions.includes('edit_dam')"
                                :current="assetTransform.asset_folder_id == null ? 0 : assetTransform.asset_folder_id"
                                :selected="assetTransform.asset_folder_id == null ? 0 : assetTransform.asset_folder_id"
                            ></select-folder>
                        </div>
                    </div>
                    <div class="row w-100 pb-0 col-md-12">
                        <div class="col-md-6 d-flex flex-column">
                            <label for="published">Published</label>
                            <input type="checkbox"
                                name="published"
                                readonly
                                :disabled="permissions.length && !permissions.includes('edit_dam')"
                                style="width: 38px;"
                                class="form-control asset-private"
                                v-model="editableAssetProperties.private"
                            />
                        </div>
                    </div>
                    <div class="row w-100 pb-0 col-md-12">
                        <div class="col-md-6 d-flex flex-column">
                            <label for="">Add Tags</label>
                            <div>
                                <input data-id="asset-tags" type="text" name="" id="" :disabled="!permissions.includes('edit_dam')" class="form-control" v-model="pendingTag" @keypress.enter="handleNewTag">
                            </div>
                            <div class="text-muted mt-1">
                                Press 'Enter' to add tags
                            </div>
                        </div>
                        <div class="col-md-6 d-flex flex-column" v-if="editableAssetProperties.tags.length">
                            <label for="published">Existing Tags</label>
                            <div style="min-height: 38px;">
                                <div class="asset-tag-cloud" v-if="permissions.includes('edit_dam')">
                                    <button class="badge badge-primary pending-tag" v-for="tag in editableAssetProperties.tags" :key="tag" @click="handleRemoveTag(tag)">
                                        {{ tag }}
                                    </button>
                                </div>
                                <div class="asset-tag-cloud" v-else>
                                    <button class="badge badge-primary pending-tag" v-for="tag in editableAssetProperties.tags" :key="tag">
                                        {{ tag }}
                                    </button>
                                </div>
                            </div>
                            <div class="text-muted mt-1">
                                Click to remove
                            </div>
                        </div>
                    </div>
                    <div class="row w-100 pb-0 col-md-12">
                        <div class="col-md-6">
                            <button v-if="permissions.length && permissions.includes('edit_dam')" @click="handleUpdateAsset" class="btn btn-outline-primary">
                                Save
                            </button>
                            <button data-id="asset-delete-toggle" v-if="permissions.length && permissions.includes('delete_dam')" @click="deleteAssetModal = true" class="btn btn-danger">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
                <div class="mb-4 mx-0 pb-0 row w-100"  v-if="features.includes('dam_advanced') && selectedTab === 'versions' && assetTransform.master_asset_id == null">
                    <asset-versions :asset="asset" :asset-types-map="assetTypesMap"></asset-versions>
                </div>
                <div class="mb-4 mx-0 pb-0 row w-100" v-if="permissions.includes('edit_dam') && selectedTab === 'linkeditems' && assetTransform.master_asset_id == null">
                    <asset-linked :asset="asset" :asset-types-map="assetTypesMap"></asset-linked>
                </div>
                <div class="mb-4 mx-0 pb-0 row w-100" v-if="features.includes('dam_advanced') && selectedTab === 'transformations' && assetTransform.master_asset_id == null && assetTypesMap.displayableImageTypes.includes(assetTransform.asset_type_id)">
                    <asset-transform :asset="assetTransform" :asset-types-map="assetTypesMap" :is-safari="isSafari" :is-ie="isIE" :is-chromium="isChromium" @refresh-transform="refreshAsset($event)"></asset-transform>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Lightbox from './lightbox.vue';
import ProductAlerts from "../products/product-details-alerts.vue";
import SelectFolder from "./select-folder.vue";
import axios from 'axios'
import AssetLinked from './asset-linked.vue';
import AssetTransform from './asset-transform.vue';
import AssetVersions from './asset-versions.vue';

const base_url = window.location.protocol + "//" + window.location.host;

export default {
    name: "dam-asset",
    props: {
        asset: {
            required: true,
            type: Object
        },
    },
    components: {
        "lightbox": Lightbox,
        "select-folder": SelectFolder,
        "alerts": ProductAlerts,
        "asset-transform": AssetTransform,
        "asset-linked": AssetLinked,
        "asset-versions": AssetVersions
    },
    data() {
        return {
            editableAssetProperties: {
                name: '',
                private: true,
                assetFolderId: 0,
                tags: []
            },
            deleteAssetModal: false,
            pendingTag: '',
            lightbox: false,
            selectedTab: 'general',
            alert: {
                message: "",
                status: "",
                timeOut: 15000,
            },
            assetTransform: {}
        };
    },
    mounted() {
        this.assetTransform = this.asset;
        this.editableAssetProperties.name = this.assetTransform.name;
        this.editableAssetProperties.private = this.assetTransform.private;
        this.editableAssetProperties.tags =
            this.assetTransform.tags == null ? [] : this.assetTransform.tags.split(",");
    },
    methods: {
        handleNewTag() {
            let tag = this.pendingTag.trim();

            if (this.editableAssetProperties.tags.includes(tag)) {
                this.alert.status = "alert-warning";
                this.alert.message = "Tag exists already"
            }

            if (tag.length && !this.editableAssetProperties.tags.includes(tag)) {
                this.editableAssetProperties.tags.push(tag);
                this.pendingTag = '';

                this.$nextTick(function() {
                    this.handleUpdateAsset();
                });
            }

        },
        handleRemoveTag(tag) {
            this.editableAssetProperties.tags = this.editableAssetProperties.tags.filter((i) => i !== tag);

            this.$nextTick(function() {
                this.handleUpdateAsset();
            });
        },
        confirmDeleteAsset() {
            axios.delete('/pimapi/assets/' + this.assetTransform.id)
                .then(function (response) {
                    this.alert.status = "alert-success";
                    this.alert.message = 'Success, asset deleted.';

                    this.deleteAssetModal = false;

                    setTimeout(function() {
                        let url = this.assetTransform.asset_folder_id ?
                            `/assets/${this.assetTransform.asset_folder_id}` : '/assets';

                        window.location.push(url);
                    }, 2500)
                }.bind(this))
                .catch(function (err) {
                    console.log(err);
                    this.deleteAssetModal = false;
                    this.alert.status = "alert-danger";
                    this.alert.message = 'An error occurred, please try again later.';
                }.bind(this));
        },
        handleCloseLightbox() {
            this.lightbox = false;
        },
        handleUpdateAsset() {
            let payload = {
                name: this.editableAssetProperties.name,
                assetFolderId: this.editableAssetProperties.assetFolderId == null ? 0 : this.editableAssetProperties.assetFolderId,
            }

            if (this.editableAssetProperties.tags.length) {
                payload.tags = this.tagArrayToString;
            }

            axios.put(base_url + '/pimapi/assets/' + this.assetTransform.id, payload)
            .then(function(response) {
                this.alert.status = "alert-success";
                this.alert.message = 'Success, asset updated.';
            }.bind(this))
            .catch(function(err) {
                console.log(err);
                this.alert.status = "alert-danger";
                this.alert.message = err.response.data.message ||
                    'There was an error, please try again later.';
            }.bind(this));
        },
        handleFolderSelection(value) {
            this.editableAssetProperties.assetFolderId = value;
        },
        refreshAsset(payload) {
            this.assetTransform = payload;
        }
    },
    watch: {
        selectedLinkType: function(value, oldValue) {
            this.selectedEntity.id = '';
        },
        asset: {
            immediate: true,
            handler: function(value, oldValue) {
                this.editableAssetProperties.name = value.name;
                this.editableAssetProperties.private = value.private;
                this.editableAssetProperties.assetFolderId =
                    typeof value.asset_folder_id == null ? 0 : value.asset_folder_id;
            }
        },
    },
    computed: {
        assetTypesMap() {
            return {
                displayableImageTypes: [1, 2, 3, 4, 26],
                displayableVideoTypes: this.isSafari ? [28, 31] : [28, 29, 31],
                documentPlaceholderTypes: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                videoPlaceholderTypes: [27, 30, 32, 33, 34, 35, 36, 37, 38, 39],
                imagePlaceholderTypes: [24, 25],
                genericPlaceholderTypes: [16, 17, 18, 19, 20, 21, 22, 23, 40, 41]
            }
        },
        tagArrayToString() {
            return this.editableAssetProperties.tags.join(",");
        },
        permissions() {
            return this.$parent.enabledPermissions ? this.$parent.enabledPermissions : [];
        },
        features() {
            return this.$parent.enabledFeatures ? this.$parent.enabledFeatures : [];
        },
        isChromium() {
            return navigator.userAgent.indexOf("Chrome") > -1;
        },
        isSafari() {
            return navigator.userAgent.indexOf("Safari") > -1 && !this.isChromium
        },
        isIE() {
            return navigator.userAgent.indexOf("MSIE") > -1;
        }
    },
};
</script>
<style lang="scss" scoped>
    .dam-asset__info-column {
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        height: 100%;
        width: 100%;

        & label {
            font-weight: bold;
        }
    }

    .dam-asset__asset-preview {
        overflow: hidden;
        border-radius: 3px;
        border: 1px solid #dedede;

        & img {
            object-fit: contain;
            width: 100%;
            height: 100%;
            cursor: pointer;
        }
    }

    .asset-preview__icon-outer {
        width: 100%;
        height: 0;
        background: white;
        cursor: pointer;
        overflow: hidden;
        padding-top: 56.25%;
        position: relative;
    }

    .asset-preview__icon-inner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    .dam-asset__preview-column {
        margin: 0;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
    }

    .dam-asset__section-title {
        width: 100%;
        border-bottom: 1px solid #dedede;
    }

    .asset-version-list {
        ::v-deep thead {
            display: none;
        }

        ::v-deep td {
            padding: 0.5rem 0 !important;
        }
    }

    .nav-link {
        border: 0;
        font-size: 12px;
        color: #576670;
        text-align: left;
        padding-left: 16px !important;
        cursor: pointer;
        border-radius: 0;
    }

    .modal-wrapper {
        z-index: 9999;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        .modal-bg {
            background: rgba(255,255,255,0.5);
            width: 100%;
            height: 100%;
            position: absolute;
            z-index: 99991;
        }

        .modal-inner {
            border: 1px solid #dedede;
            z-index: 99992;
            position: absolute;
            background: white;
        }
    }

    .asset-tag-cloud {
        display: flex;
        flex-wrap: wrap;
        grid-gap: 0.25rem;

        button {
            cursor: pointer;
        }
    }

    @media (min-width: 1100px) {
        .asset-preview__icon-outer {
            padding-top: 100%;
        }
    }

    .pending-tag {
        text-transform: none !important;
    }
</style>
